import React, { useState, useEffect } from "react";
import { classNames } from "primereact/utils";
import styles from "./BookingSuccessStep.module.scss";
import { IService } from "../../models/IService";
import { useAuthentication } from "../../../../../common/infrastructure/hooks/useAuthentication";
import { useAppContext } from "../../../../../AppContext";
import { IEmployeeAvailability } from "../../models";
import { PriceDisplay } from "../../../../../common/components/PriceDisplay";
import { AddToCalendarButton } from "../../../../../common/components/calendar/AddToCalendarButton";
import { NotificationPermissionDialog } from "../../../../notifications/NotificationPermissionDialog";

interface IBookingSuccessStepProps {
  className?: string;
  selectedService?: IService | null;
  selectedEmployee?: IEmployeeAvailability | null;
  selectedDate?: Date | null;
  selectedTime?: string | null;
  totalPrice?: number;
  bookingId?: string;
  onBookAnother?: () => void;
  onGoToDashboard?: () => void;
}

export const BookingSuccessStep = ({
  className,
  selectedService,
  selectedEmployee,
  selectedDate,
  selectedTime,
  totalPrice,
  bookingId,
  onBookAnother,
  onGoToDashboard,
}: IBookingSuccessStepProps) => {
  const { currentUser } = useAuthentication();
  const { selectedStudio } = useAppContext();
  const [showDetails, setShowDetails] = useState(false);
  const [celebrationActive, setCelebrationActive] = useState(false);

  // Generate booking ID if not provided
  const finalBookingId =
    bookingId ||
    `BR${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(
      2,
      "0"
    )}${String(new Date().getDate()).padStart(2, "0")}-${String(
      Math.floor(Math.random() * 1000)
    ).padStart(3, "0")}`;

  // Trigger celebration animation after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setCelebrationActive(true);
    }, 500);

    const detailsTimer = setTimeout(() => {
      setShowDetails(true);
    }, 1500);

    return () => {
      clearTimeout(timer);
      clearTimeout(detailsTimer);
    };
  }, []);

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString("bg-BG", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };


  const formatDuration = (duration: number): string => {
    if (duration < 60) {
      return `${duration} мин.`;
    }
    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;
    if (minutes === 0) {
      return `${hours} ч.`;
    }
    return `${hours} ч. ${minutes} мин.`;
  };

  const handleBookAnother = () => {
    if (onBookAnother) {
      onBookAnother();
    }
  };

  const handleGoToDashboard = () => {
    if (onGoToDashboard) {
      onGoToDashboard();
    }
  };

  return (
    <div className={classNames(styles.container, className)}>
      {/* Success Animation */}
      <div className={styles.successAnimation}>
        <div
          className={classNames(styles.successIcon, {
            [styles.celebrating]: celebrationActive,
          })}
        >
          <span className={styles.checkmark}>✓</span>

          {/* Celebration particles */}
          {celebrationActive && (
            <>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.1s",
                    "--x": "-20px",
                    "--y": "-30px",
                  } as React.CSSProperties
                }
              >
                🎉
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.2s",
                    "--x": "25px",
                    "--y": "-25px",
                  } as React.CSSProperties
                }
              >
                ✨
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.3s",
                    "--x": "-30px",
                    "--y": "20px",
                  } as React.CSSProperties
                }
              >
                🎊
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.4s",
                    "--x": "30px",
                    "--y": "25px",
                  } as React.CSSProperties
                }
              >
                ⭐
              </div>
              <div
                className={styles.particle}
                style={
                  {
                    "--delay": "0.5s",
                    "--x": "0px",
                    "--y": "-40px",
                  } as React.CSSProperties
                }
              >
                🎈
              </div>
            </>
          )}
        </div>

        <h1 className={styles.successTitle}>Ще ви очакваме! 🎉</h1>
        <p className={styles.successSubtitle}>
          Вашата резервация е потвърдена! Ще получите имейл за потвърждение.
        </p>
      </div>

      {/* Booking Details */}
      {showDetails && (
        <div className={styles.bookingDetails}>
          <div className={styles.bookingHeader}>
            <h2 className={styles.bookingTitle}>Детайли за резервацията</h2>
          </div>

          <div className={styles.detailsGrid}>
            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>🏢</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Обект</span>
                <span className={styles.detailValue}>
                  {selectedStudio?.name}
                </span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>✂️</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Услуга</span>
                <span className={styles.detailValue}>
                  {selectedService?.name}
                </span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>👤</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Специалист</span>
                <span className={styles.detailValue}>
                  {selectedEmployee?.name}
                </span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>📅</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Дата и час</span>
                <span className={styles.detailValue}>
                  {selectedDate && formatDate(selectedDate)} в {selectedTime}
                </span>
              </div>
            </div>

            <div className={styles.detailCard}>
              <div className={styles.detailIcon}>🕐</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Продължителност</span>
                <span className={styles.detailValue}>
                  {selectedService && formatDuration(selectedService.duration)}
                </span>
              </div>
            </div>

            <div className={classNames(styles.detailCard, styles.priceCard)}>
              <div className={styles.detailIcon}>💰</div>
              <div className={styles.detailContent}>
                <span className={styles.detailLabel}>Обща сума</span>
                <span
                  className={classNames(styles.detailValue, styles.priceValue)}
                >
                  {selectedService && (
                    <PriceDisplay
                      minPrice={selectedService.minPrice}
                      maxPrice={selectedService.maxPrice}
                    />
                  )}
                </span>
              </div>
            </div>
          </div>

          {/* Customer Information */}
          {currentUser && (
            <div className={styles.customerCard}>
              <h3 className={styles.customerTitle}>Информация за клиента</h3>
              <div className={styles.customerDetails}>
                <div className={styles.customerInfo}>
                  <span className={styles.customerLabel}>Име:</span>
                  <span className={styles.customerValue}>
                    {currentUser.firstName} {currentUser.lastName}
                  </span>
                </div>
                {currentUser.phoneNumber && (
                  <div className={styles.customerInfo}>
                    <span className={styles.customerLabel}>Телефон:</span>
                    <span className={styles.customerValue}>
                      {currentUser.phoneNumber}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Important Reminders */}
      {showDetails && (
        <div className={styles.reminders}>
          <h3 className={styles.remindersTitle}>Важна информация</h3>
          <div className={styles.remindersList}>
            <div className={styles.reminderItem}>
              <span className={styles.reminderIcon}>⏰</span>
              <span className={styles.reminderText}>
                Моля, пристигнете поне 5 минути преди часа на резервацията
              </span>
            </div>
            <div className={styles.reminderItem}>
              <span className={styles.reminderIcon}>📱</span>
              <span className={styles.reminderText}>
                Ще получите Email потвърждение след завършване на резервацията
              </span>
            </div>
            <div className={styles.reminderItem}>
              <span className={styles.reminderIcon}>⚡️</span>
              <span className={styles.reminderText}>
                Можете да контролирате резервациитe си във Вашия профил.
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {showDetails && (
        <div className={styles.actionsSection}>
          <div className={styles.primaryActions}>
            <AddToCalendarButton
              reservationId={bookingId ? Number(bookingId) : undefined}
              className={styles.primaryButton}
            />

            <button
              className={styles.secondaryButton}
              onClick={handleGoToDashboard}
              type="button"
            >
              <span className={styles.buttonIcon}>📊</span>
              <span className={styles.buttonText}>Моите резервации</span>
            </button>
          </div>
        </div>
      )}
      <NotificationPermissionDialog
        isConfirmedReservation
        studioName={selectedStudio?.name}
        eventDate={selectedDate}
        startTime={selectedTime}
      />
    </div>
  );
};
